import React, { useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { reduxForm } from "redux-form";
import { cloneDeep } from "lodash";

import useQueryParams from "hooks/useQueryParams";
import { clearRefinements } from "reducers/Refinements";
import {
	reset,
	loadConceptWithVariables,
	loadCreativesWithVariables,
	createVersionsAndAssets
} from "reducers/VersionGeneration";
import { asyncValidateVersionPrefixExists } from "widgets/modals/versions/generate/validators";
import CurrentVersionsBlade from "./CurrentVersionsBlade";
import GeneratedVersionsBlade from "./GeneratedVersionsBlade";
import { AI_GENERATE_VERSIONS_FORM, MODES } from "./constants";

import { setKey, removeKey } from "utils/Keyboard";
import {
	VERSION_GEN_SELECTED_PREVIEW_KEY,
	EXIT_AI_DEV_MODE_KEY,
	enableAIDevMode,
	disableAIDevMode
} from "utils/Flags";

import "./AIGenerateVersionsBlade.css";


const BLADES_BY_STEP = [
	CurrentVersionsBlade,
	GeneratedVersionsBlade,
];

function GenerateVersionsConceptBlade(props){
	const {
		libraryId,
		conceptId,
		returnPath,
	} = props;

	const dispatch = useDispatch();
	const { getParam } = useQueryParams();
	const creativeIdsParam = getParam("creativeIds");

	const mode = conceptId ? MODES.CONCEPT : MODES.CREATIVES;

	useEffect(() => {
		setKey(VERSION_GEN_SELECTED_PREVIEW_KEY, enableAIDevMode);
		setKey(EXIT_AI_DEV_MODE_KEY, disableAIDevMode);

		return () => {
			removeKey(VERSION_GEN_SELECTED_PREVIEW_KEY, EXIT_AI_DEV_MODE_KEY);
			// Clear refinements data
			dispatch(clearRefinements());
			// Reset the version generation state
			dispatch(reset());
		};
	}, []);

	useEffect(() => {
		if(conceptId){
			dispatch(loadConceptWithVariables(libraryId, conceptId));
		} else if(creativeIdsParam){
			const creativeIds = creativeIdsParam.split(",");
			dispatch(loadCreativesWithVariables(libraryId, creativeIds));
		} else {
			//If we do not pass creative ids as a query string parameter and have
			//direct urls in versions there is already
			//creatiev id in URL and we can extract it from there
			//So code below will ensure that when we go directly to the link
			//.../library/:libraryId/creative/:creativeId/versions/modal/generate-versions
			//it will load...
			const creativeIds = [returnPath.match(/\/creative\/(\d+)\/versions/)[1]];
			dispatch(loadCreativesWithVariables(libraryId, creativeIds));
		}
	}, [libraryId, conceptId, creativeIdsParam]);

	const [step, setStep] = React.useState(0);

	const Blade = BLADES_BY_STEP[step];

	function incrementStep(){
		if(step < BLADES_BY_STEP.length - 1){
			setStep(step + 1);
		}
	}

	function decrementStep(){
		if(step > 0){
			setStep(step - 1);
		}
	}

	return (
		<Blade
			{...props}
			mode = {mode}
			onNext = {incrementStep}
			onBack = {decrementStep}
		/>
	);
}

function mapStateToProps(state){
	return {
		versionGenerationState: state.versionGeneration,
		refinementsState: state.refinements,
	};
}

export default connect(mapStateToProps)(
	reduxForm({
		form: AI_GENERATE_VERSIONS_FORM,
		initialValues: {
			versionNamePrefix: "",
			sourceVersion: "default",
			selectedVariations: {},
		},
		asyncValidate: asyncValidateVersionPrefixExists,
		asyncBlurFields: ["versionNamePrefix"],
		// Avoid async validation on submit as by this time we know prefix is good
		// as the check was part of the first step and happenned before generation
		shouldAsyncValidate: ({ trigger }) => (trigger !== "submit"),
		onSubmit(values, dispatch, formProps){
			const {
				libraryId,
				versionGenerationState,
				refinementsState,
				returnPath,
			} = formProps;
			const { versionNamePrefix, selectedVariations } = values;
			const { assetRefinements, variationRefinements }  = refinementsState;

			const { creatives: sourceCreatives, sourceVersions } = versionGenerationState;
			const creativeSizeMap = sourceCreatives.reduce((acc, creative) => {
				acc[creative.id] = { width: creative.width, height: creative.height };
				return acc;
			}, {});

			const assets = [];
			const timestamp = Date.now();

			// Remove any creatives that don't have variations selected
			const selectedVariationEntries = Object.entries(selectedVariations).filter(([creativeId, { variations }]) => variations.length);

			const creatives = selectedVariationEntries.map(([creativeId, { variations }]) => {
				const sourceVersion = sourceVersions.find(source => `${source.creative.id}` === `${creativeId}`);
				const versions = variations.map((variation, index) => {
					const variableConfig = cloneDeep(sourceVersion.variableConfig);
					Object.entries(variation).forEach(([variableName, value]) => {
						const existingVariable = variableConfig[variableName];
						if(existingVariable){
							const type = existingVariable?.type;
							if(/text/i.test(type)){
								existingVariable.data = value;
							} else if(/image/i.test(type)){
								existingVariable.prop = value;
								const { cropMetadata } = existingVariable;
								const { width, height } = creativeSizeMap[creativeId];
								const dimensions = `${width}x${height}`;
								// If we have a custom variation refinement for this asset, use it otherwise use the default asset refinement
								let canvas = variationRefinements?.[index]?.[dimensions] ?? assetRefinements?.[value]?.[dimensions];
								canvas = {
									left: Math.round(canvas.left),
									top: Math.round(canvas.top),
									width: Math.round(canvas.width),
									height: Math.round(canvas.height),
								};
								existingVariable.cropMetadata = {
									...cropMetadata,
									asset: {
										width: 1024,
										height: 1024,
									},
									canvas,
									hasManualRefinements: true,
								};

								const pathname = URL.parse(value)?.pathname;
								if(pathname){
									const filename = pathname.split("/").pop();
									const extension = filename.split(".").pop();

									const existingAsset = assets.find(asset => asset.path === value);
									const data = existingAsset ? existingAsset.name : `${versionNamePrefix}_${timestamp}_${assets.length}.${extension}`;
									existingVariable.data = data;

									if(!existingAsset){
										assets.push({
											name: data,
											path: value,
											isAiGen: true,
										});
									}
								}
							}
						}
					});

					return {
						name: `${versionNamePrefix} - Variation ${index + 1}`,
						variableConfig,
					};
				});

				return {
					id: creativeId,
					versions,
				};
			});

			const data = {
				assets,
				creatives,
			};

			return dispatch(createVersionsAndAssets(libraryId, data)).then((response) => {
				if(response?.success){
					dispatch(push(returnPath));
				}
			});
		},
	})(GenerateVersionsConceptBlade)
);
